import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { AUTH_TYPE } from '../types/auth';
import { isNoLoaderUrl } from '../utils/stringUtils';

type UserContextType = {
  token: string | null;
  uploadStatus: string | null;
  isLoading: boolean;
  tokenRefreshAt: number | null;
  refreshed: boolean;
  connectionType: string | null;
  isBulkProcessing: boolean;
  forceReload: () => void;
  setUser: (token: string | null) => void;
  setLoader: (loading: boolean, url?: string) => void;
  setConnection: (connection: string | null) => void;
  setDocumentUploadStatus: (status: string | null) => void;
  setBulkProcessing: (loading: boolean, url?: string) => void;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshed, setRefreshed] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [tokenRefreshAt, setTokenRefreshAt] = useState<number | null>(null);
  const [connectionType, setConnectionType] = useState<string | null>(null);
  const [isBulkProcessing, setIsBulkProcessing] = useState<boolean>(false);

  const setUser = (token: string | null) => {
    setTokenRefreshAt(Date.now() + 1000 * 60 * 30); // set token refresh time to 30 min
    setToken(token);
  };

  const setLoader = (loading: boolean, url?: string) => {
    if (!url || !isNoLoaderUrl(url)) {
      setIsLoading(loading);
    }
  };

  const forceReload = () => {
    setRefreshed(true);
  };

  const setConnection = (conn: string | null) => {
    let connection = null;
    if (conn) {
      // Check if conn includes either of the connection types
      connection = conn.includes(AUTH_TYPE.External) ? AUTH_TYPE.External : AUTH_TYPE.Internal;
    }
    setConnectionType(connection);
  };

  const setDocumentUploadStatus = (status: string | null) => {
    setUploadStatus(status);
  };

  const setBulkProcessing = (status: boolean) => {
    setIsBulkProcessing(status);
  };

  return (
    <UserContext.Provider
      value={{
        token,
        isLoading,
        tokenRefreshAt,
        refreshed,
        connectionType,
        uploadStatus,
        isBulkProcessing,
        setUser,
        setLoader,
        forceReload,
        setConnection,
        setDocumentUploadStatus,
        setBulkProcessing,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
export default UserProvider;

export const useUserContext = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
